import { Lock, Trophy } from 'lucide-react';
import type { CmsBlock } from './BlockRenderer';

// ─── Types & mock data ────────────────────────────────────────────────────────

type Rarity = 'COMMON' | 'UNCOMMON' | 'RARE' | 'EPIC' | 'LEGENDARY';

interface Badge {
  id: string;
  name: string;
  rarity: Rarity;
  unlocked: boolean;
  unlockedAt?: string;
}

const BADGES: Badge[] = [
  { id: '1', name: 'Primo Accesso', rarity: 'COMMON', unlocked: true, unlockedAt: '19/03/2026' },
  {
    id: '2',
    name: 'Settimana di Fuoco',
    rarity: 'RARE',
    unlocked: true,
    unlockedAt: '19/03/2026',
  },
  { id: '3', name: 'Guru dei Pronostici', rarity: 'EPIC', unlocked: false },
  { id: '4', name: 'Leggenda Lottomatica', rarity: 'LEGENDARY', unlocked: false },
  { id: '5', name: 'Re dei Social', rarity: 'UNCOMMON', unlocked: false },
  { id: '6', name: 'Contest Champion', rarity: 'RARE', unlocked: false },
];

const RARITY_COLORS: Record<Rarity, string> = {
  COMMON: '#00d492',
  UNCOMMON: '#3b82f6',
  RARE: '#a855f7',
  EPIC: '#f97316',
  LEGENDARY: '#f59e0b',
};

const RARITY_GRADIENTS: Record<Rarity, string> = {
  COMMON: 'from-[#00bc7d] to-[#00786f]',
  UNCOMMON: 'from-[#3b82f6] to-[#1d4ed8]',
  RARE: 'from-[#a855f7] to-[#7e22ce]',
  EPIC: 'from-[#f97316] to-[#c2410c]',
  LEGENDARY: 'from-[#f59e0b] to-[#b45309]',
};

// ─── Circular progress SVG ────────────────────────────────────────────────────

function CircularProgress({ pct }: { pct: number }) {
  const r = 45;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct / 100);

  return (
    <div className="relative w-24 h-24 flex items-center justify-center">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
        {/* Track */}
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="#1d293d"
          strokeWidth="8"
        />
        {/* Progress */}
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="#00bc7d"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <span className="absolute text-white font-black text-xl">{pct}%</span>
    </div>
  );
}

// ─── Badge card ───────────────────────────────────────────────────────────────

function BadgeCard({ badge }: { badge: Badge }) {
  if (badge.unlocked) {
    return (
      <div className="bg-[#0f172b] border border-white/5 rounded-3xl flex flex-col items-center py-6 px-4 gap-3 shadow-[0_4px_6px_0_rgba(0,0,0,0.1)]">
        <div
          className={`w-16 h-16 rounded-full bg-gradient-to-br ${RARITY_GRADIENTS[badge.rarity]} flex items-center justify-center shadow-[0_10px_15px_0_rgba(0,188,125,0.2),0_4px_6px_0_rgba(0,188,125,0.2)]`}
        >
          <Trophy size={28} className="text-white" />
        </div>
        <p className="text-white font-bold text-sm text-center leading-5">{badge.name}</p>
        <div className="flex flex-col items-center gap-1">
          <p
            className="font-bold text-[10px] uppercase tracking-widest"
            style={{ color: RARITY_COLORS[badge.rarity] }}
          >
            {badge.rarity}
          </p>
          {badge.unlockedAt && (
            <p className="text-[#62748e] text-[10px]">Sbloccato: {badge.unlockedAt}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0f172b] border border-white/5 rounded-3xl flex flex-col items-center py-6 px-4 gap-3 opacity-50">
      <div className="w-16 h-16 rounded-full bg-[#1d293d] flex items-center justify-center">
        <Lock size={28} className="text-[#62748e]" />
      </div>
      <p className="text-[#62748e] font-bold text-sm text-center leading-5">{badge.name}</p>
      <p
        className="font-bold text-[10px] uppercase tracking-widest"
        style={{ color: RARITY_COLORS[badge.rarity] }}
      >
        {badge.rarity}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LoyaltyBadgesBlock(_: { block?: CmsBlock }) {
  const unlocked = BADGES.filter((b) => b.unlocked).length;
  const total = BADGES.length;
  const pct = Math.round((unlocked / total) * 100);

  return (
    <div className="p-10 flex flex-col gap-8">
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-white font-black text-[36px] leading-10 tracking-tight">
          Collezione Badge
        </h1>
        <p className="text-[#90a1b9] text-base mt-1">
          Dimostra la tua abilità sbloccando i badge. Ogni badge racconta una storia delle tue
          vittorie e ti garantisce bonus permanenti all&apos;interno della piattaforma.
        </p>
      </div>

      {/* ── Progress summary card ─────────────────────────────────────────── */}
      <div className="bg-[#0f172b] border border-white/5 rounded-[32px] flex items-center gap-8 px-10 py-6 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]">
        <CircularProgress pct={pct} />

        <div className="flex flex-col gap-1">
          <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-wider">
            Completamento
          </p>
          <h2 className="text-white font-bold text-xl leading-7">Bacheca Trofei</h2>
        </div>

        {/* Divider */}
        <div className="w-px h-12 bg-white/10 mx-4" />

        <div className="flex items-center gap-10">
          <div>
            <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-wider mb-1">
              Sbloccati
            </p>
            <p className="text-white font-black text-4xl leading-none">{unlocked}</p>
          </div>
          <div>
            <p className="text-[#90a1b9] font-bold text-xs uppercase tracking-wider mb-1">
              Totali
            </p>
            <p className="text-white font-black text-4xl leading-none">{total}</p>
          </div>
        </div>
      </div>

      {/* ── Badge grid ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {BADGES.map((badge) => (
          <BadgeCard key={badge.id} badge={badge} />
        ))}
      </div>
    </div>
  );
}
